/**
 * AuditTimeline — renders a list of audit entries as a timeline, with
 * click-to-expand detail fetch for before/after state snapshots.
 *
 * Phase 13 Cluster A — Feature D.
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { formatDateTime } from '@armard/shared';
import {
  AUDIT_ACTION_COLORS,
  AUDIT_ACTION_LABELS,
  auditEntityLabel,
  auditUserLabel,
  useAuditEntry,
  type AuditListEntry,
} from '../../hooks/useAuditLog';
import { LoadingSpinner } from '../ui';
import type { AuditAction } from '@armard/shared';

interface AuditTimelineProps {
  entries: AuditListEntry[];
  showEntityLink?: boolean;
  emptyMessage?: string;
}

export function AuditTimeline({
  entries,
  showEntityLink = false,
  emptyMessage = 'Keine Einträge vorhanden.',
}: AuditTimelineProps) {
  if (entries.length === 0) {
    return (
      <p className="text-center py-6 text-white/40 text-sm">{emptyMessage}</p>
    );
  }

  return (
    <ul className="space-y-2">
      {entries.map((entry) => (
        <AuditTimelineRow key={entry.id} entry={entry} showEntityLink={showEntityLink} />
      ))}
    </ul>
  );
}

interface AuditTimelineRowProps {
  entry: AuditListEntry;
  showEntityLink: boolean;
}

function AuditTimelineRow({ entry, showEntityLink }: AuditTimelineRowProps) {
  const [expanded, setExpanded] = useState(false);
  const actionColor = AUDIT_ACTION_COLORS[entry.action as AuditAction] ?? AUDIT_ACTION_COLORS.update;
  const actionLabel = AUDIT_ACTION_LABELS[entry.action as AuditAction] ?? entry.action;

  return (
    <li className="bg-white/3 border border-white/6 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="w-full text-left px-4 py-3 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-start gap-3 flex-wrap">
          <span className={`px-2 py-0.5 rounded text-xs font-medium border ${actionColor}`}>
            {actionLabel}
          </span>
          <span className="text-white/80 text-sm font-medium">
            {auditEntityLabel(entry.entityType)}
          </span>
          {showEntityLink && (
            <EntityLink entityType={entry.entityType} entityId={entry.entityId} />
          )}
          <span className="text-white/40 text-xs ml-auto">
            {formatDateTime(entry.timestamp)}
          </span>
        </div>
        <div className="mt-1 flex items-center gap-2 flex-wrap text-xs">
          <span className="text-white/50">{auditUserLabel(entry)}</span>
          {entry.changedFields && entry.changedFields.length > 0 && (
            <>
              <span className="text-white/30">·</span>
              <span className="text-white/40">
                {entry.changedFields.length} Feld
                {entry.changedFields.length !== 1 ? 'er' : ''}:{' '}
                <span className="text-white/60">
                  {entry.changedFields.slice(0, 4).join(', ')}
                  {entry.changedFields.length > 4 ? ' …' : ''}
                </span>
              </span>
            </>
          )}
          <span className="ml-auto text-white/30">
            {expanded ? '▾ Details ausblenden' : '▸ Details anzeigen'}
          </span>
        </div>
      </button>
      {expanded && <AuditDetail entryId={entry.id} />}
    </li>
  );
}

function EntityLink({ entityType, entityId }: { entityType: string; entityId: string }) {
  // Only linkable entity types are surfaced as links; others render plain.
  if (entityType === 'invoice') {
    return (
      <Link
        to="/invoices/$id"
        params={{ id: entityId }}
        className="text-xs text-white/50 hover:text-white transition-colors font-mono"
        onClick={(e) => e.stopPropagation()}
      >
        {entityId.slice(0, 8)}…
      </Link>
    );
  }
  if (entityType === 'customer') {
    return (
      <Link
        to="/customers/$id"
        params={{ id: entityId }}
        className="text-xs text-white/50 hover:text-white transition-colors font-mono"
        onClick={(e) => e.stopPropagation()}
      >
        {entityId.slice(0, 8)}…
      </Link>
    );
  }
  return (
    <span className="text-xs text-white/40 font-mono" title={entityId}>
      {entityId.slice(0, 8)}…
    </span>
  );
}

function AuditDetail({ entryId }: { entryId: string }) {
  const { data, isLoading, error } = useAuditEntry(entryId);

  if (isLoading) {
    return (
      <div className="border-t border-white/6 px-4 py-4 flex justify-center">
        <LoadingSpinner size="sm" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="border-t border-white/6 px-4 py-3 text-sm text-red-400">
        Details konnten nicht geladen werden.
      </div>
    );
  }

  const showBefore = data.beforeState && Object.keys(data.beforeState).length > 0;
  const showAfter = data.afterState && Object.keys(data.afterState).length > 0;

  return (
    <div className="border-t border-white/6 px-4 py-4 space-y-3 bg-white/[0.02]">
      {showBefore && (
        <div>
          <h4 className="text-xs uppercase tracking-wider text-white/40 mb-1.5">Vorher</h4>
          <pre className="text-xs text-white/70 overflow-x-auto bg-black/30 rounded p-2 border border-white/5">
            {JSON.stringify(data.beforeState, null, 2)}
          </pre>
        </div>
      )}
      {showAfter && (
        <div>
          <h4 className="text-xs uppercase tracking-wider text-white/40 mb-1.5">Nachher</h4>
          <pre className="text-xs text-white/70 overflow-x-auto bg-black/30 rounded p-2 border border-white/5">
            {JSON.stringify(data.afterState, null, 2)}
          </pre>
        </div>
      )}
      {!showBefore && !showAfter && (
        <p className="text-xs text-white/40">Keine State-Snapshots gespeichert.</p>
      )}
    </div>
  );
}
